import { mdiDotsVertical, mdiShieldCheck } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import { useMemo, useState } from 'react';
import { useMutation, useApolloClient } from '@apollo/client/react';

import {
    ListItem,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import {
    ICertificateValidationStatus,
    DeleteReceivedCertificatesDocument,
    IReceivedCertificateFragment,
    UpdateReceivedCertificatesDocument,
} from 'graphql/types';
import { formatDate } from 'common/utils/formatDate';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { IconButton } from 'common/components/IconButton';
import { useCanManageUser } from 'user/hooks';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { ReceivedCertificateProgressChip } from 'common/components/ReceivedCertificateProgressChip';
import { ReceivedCertificatePreviewModal } from 'common/components/ReceivedCertificatePreviewModal';
import { Icon } from 'common/components/Icon';
import { parseJSON } from 'common/utils/json';

enum EStatusAction {
    Approve = 'approve',
    Reject = 'reject',
}

interface IProps {
    disableHover?: boolean;
    receivedCertificate: IReceivedCertificateFragment;
    portfolioItemId?: string;
    userId: string;
    onDelete?: () => void;
    onSetExternalStatus?: () => void;
}

export const ReceivedCertificateListItem = ({
    disableHover,
    receivedCertificate,
    portfolioItemId,
    userId,
    onDelete,
    onSetExternalStatus,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { canManageUser } = useCanManageUser(userId);
    const client = useApolloClient();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [statusAction, setStatusAction] = useState<EStatusAction | null>(
        null
    );
    const [deleteReceivedCertificate, { loading: deleteLoading }] = useMutation(
        DeleteReceivedCertificatesDocument
    );
    const [updateReceivedCertificates, { loading: statusUpdateLoading }] =
        useMutation(UpdateReceivedCertificatesDocument);

    const {
        title,
        thumbnail,
        issueDate,
        expiryDate,
        file,
        permissions: receivedCertificatePermissions,
        validationStatus,
        isExternalCertificate,
        requiresValidation,
    } = receivedCertificate || {};
    const permissions = useMemo(
        () => parseJSON(receivedCertificatePermissions),
        [receivedCertificatePermissions]
    );
    const startDate = formatDate(issueDate, 'dd MMMM yyyy');
    const endDate = expiryDate ? formatDate(expiryDate, 'dd MMMM yyyy') : '';
    const dateDisplay = `${startDate} ${endDate ? `- ${endDate}` : ''}`;

    const handleClickDelete = async () => {
        try {
            await deleteReceivedCertificate({
                variables: {
                    ids: [receivedCertificate.id],
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        client.cache.evict({ fieldName: 'portfolioReport' });

        if (portfolioItemId) {
            client.cache.evict({
                id: `PortfolioItem:${portfolioItemId}`,
            });
        }

        setShowDeleteDialog(false);

        displaySnackbar(translate('deleteReceivedCertificateSuccess'), {
            variant: 'success',
        });

        onDelete?.();
    };

    const handleSetExternalStatus = async (isApproved: boolean) => {
        try {
            await updateReceivedCertificates({
                variables: {
                    ids: [receivedCertificate.id],
                    receivedCertificate: {
                        validationStatus: isApproved
                            ? ICertificateValidationStatus.Approved
                            : ICertificateValidationStatus.Denied,
                    },
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        client.cache.evict({ fieldName: 'receivedCertificates' });
        client.cache.evict({ fieldName: 'portfolioReport' });

        if (portfolioItemId) {
            client.cache.evict({
                id: `PortfolioItem:${portfolioItemId}`,
            });
        }

        displaySnackbar(
            translate(
                isApproved
                    ? 'receivedCertificate.approveSuccess'
                    : 'receivedCertificate.rejectSuccess'
            ),
            {
                variant: 'success',
            }
        );

        setShowStatusDialog(false);

        onSetExternalStatus?.();
    };

    const showExternalMenuOptions =
        isExternalCertificate && requiresValidation && canManageUser;

    const { canUpdate = false } = permissions;

    return (
        <>
            <ListItem
                button
                disableHover={disableHover}
                px={0}
                tabIndex={-1}
                onClick={() => setShowPreviewModal(true)}
            >
                <ListItemMedia
                    color="primary"
                    image={thumbnail?.url || ''}
                    size="small"
                >
                    <Icon path={mdiShieldCheck} size="3rem" />
                </ListItemMedia>

                <ListItemText primary={title} secondary={dateDisplay} />

                {(!!file?.url || canManageUser || showExternalMenuOptions) && (
                    <ListItemSecondaryAction>
                        <DropdownMenu
                            anchor={
                                <IconButton
                                    color="inherit"
                                    iconPath={mdiDotsVertical}
                                    iconSize="2.4rem"
                                    sx={{ mr: 2 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                            }
                        >
                            {showExternalMenuOptions && canUpdate && (
                                <MenuItem
                                    disabled={
                                        validationStatus ===
                                        ICertificateValidationStatus.Approved
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setStatusAction(EStatusAction.Approve);
                                        setShowStatusDialog(true);
                                    }}
                                >
                                    {translate('receivedCertificate.approve')}
                                </MenuItem>
                            )}

                            {showExternalMenuOptions && canUpdate && (
                                <MenuItem
                                    disabled={
                                        validationStatus ===
                                        ICertificateValidationStatus.Denied
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setStatusAction(EStatusAction.Reject);
                                        setShowStatusDialog(true);
                                    }}
                                >
                                    {translate('receivedCertificate.reject')}
                                </MenuItem>
                            )}

                            {!!file?.url && (
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        file?.url &&
                                            window.open(file.url, '_blank');
                                    }}
                                >
                                    {translate('download')}
                                </MenuItem>
                            )}

                            {canManageUser && canUpdate && (
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setShowDeleteDialog(true);
                                    }}
                                >
                                    {translate('deleteReceivedCertificate')}
                                </MenuItem>
                            )}
                        </DropdownMenu>
                    </ListItemSecondaryAction>
                )}

                <ReceivedCertificateProgressChip
                    endDate={expiryDate}
                    startDate={issueDate}
                    validationStatus={validationStatus || undefined}
                />
            </ListItem>

            <ConfirmDialog
                color="error"
                confirmText={translate(
                    'deleteReceivedCertificateDialog.confirmText'
                )}
                loading={deleteLoading}
                open={!!showDeleteDialog}
                title={translate('deleteReceivedCertificateDialog.title')}
                onCancel={() => setShowDeleteDialog(false)}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleClickDelete}
            >
                {translate('deleteReceivedCertificateDialog.text')}
            </ConfirmDialog>

            {!!validationStatus && (
                <ConfirmDialog
                    color={
                        statusAction === EStatusAction.Approve
                            ? 'success'
                            : 'error'
                    }
                    confirmText={translate(
                        statusAction === EStatusAction.Approve
                            ? 'approveReceivedCertificateDialog.confirmText'
                            : 'rejectReceivedCertificateDialog.confirmText'
                    )}
                    loading={statusUpdateLoading}
                    open={showStatusDialog && !!statusAction}
                    slotProps={{
                        transition: {
                            onExited: () => setStatusAction(null),
                        },
                    }}
                    title={translate(
                        statusAction === EStatusAction.Approve
                            ? 'approveReceivedCertificateDialog.title'
                            : 'rejectReceivedCertificateDialog.title'
                    )}
                    onCancel={() => setShowStatusDialog(false)}
                    onClose={() => setShowStatusDialog(false)}
                    onConfirm={() => {
                        handleSetExternalStatus(
                            statusAction === EStatusAction.Approve
                        );
                    }}
                >
                    {translate(
                        statusAction === EStatusAction.Approve
                            ? 'approveReceivedCertificateDialog.text'
                            : 'rejectReceivedCertificateDialog.text'
                    )}
                </ConfirmDialog>
            )}

            <ReceivedCertificatePreviewModal
                canUpdate={canUpdate}
                fileUrl={file?.url || undefined}
                open={showPreviewModal}
                receivedCertificate={receivedCertificate}
                title={title}
                onClose={() => setShowPreviewModal(false)}
                onSetExternalStatus={handleSetExternalStatus}
            />
        </>
    );
};
